import { LoggingService } from '@app/core/services/logging/logging.service';
/* eslint-disable unicorn/prefer-top-level-await */
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ErrorHandler,
  LOCALE_ID,
  enableProdMode,
  importProvidersFrom,
} from '@angular/core';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  NoPreloading,
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { AppComponent } from '@app/app.component';
import {
  BookingService,
  CategoryService,
  ErrorHandlerService,
  ServiceService,
} from '@app/core/services';
import { AddressesService } from '@app/core/services/addresses/addresses.service';
import { routes } from '@app/routes';
import { icons } from '@app/shared';
import { TranslocoModule } from '@app/widgets/transloco';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';
import { enableElfProdMode } from '@ngneat/elf';
import { devTools } from '@ngneat/elf-devtools';
import { provideQueryClientOptions } from '@ngneat/query';
import { addIcons } from 'ionicons';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
  enableElfProdMode();
} else {
  devTools();
}

import 'vidstack/player';
import 'vidstack/player/layouts/default';
import 'vidstack/player/ui';

// Custom icons
addIcons(
  icons.reduce((obj: { [key: string]: any }, item) => {
    obj[item.name] = `assets/icons/${item.name}.svg`;
    return obj;
  }, {}),
);

import { TokenInterceptor } from '@app/core/interceptors/token.interceptor';

import { ServiceRequestService } from '@app/core/services/backend/service-request.service';

import { NonWorkingDayService } from '@app/core/services/backend/non-working-day.service';
import { PropertyService } from '@app/core/services/backend/property.service';
import { UserService } from '@app/core/services/backend/user.service';
import { WeatherService } from '@app/core/services/backend/weather.service';
import { NgxStripeModule } from 'ngx-stripe';

import { TutorialsService } from '@app/core/services/backend/tutorials.service';
import { QuillModule } from 'ngx-quill';

import { CommonModule } from '@angular/common';
import { LanguageInterceptor } from '@app/core/interceptors/language.interceptor';
import { AgentService } from '@app/core/services/backend/agent.service';
import { BuildingService } from '@app/core/services/backend/building.service';
import { LocationService } from '@app/core/services/backend/location.service';
import { GeolocationService } from '@app/core/services/geolocation/geolocation.service';
import { GoogleMapsService } from '@app/core/services/google-maps/google-maps.service';
import { LocaleService } from '@app/core/services/locale/locale.service';
import { BookingRepository } from '@app/store';
import { LocationRepository } from '@app/store/location.repository';

/* export function initializeDataApp(
  appResolver: AppResolver,
): () => Promise<any> {
  return (): Promise<any> => {
    return firstValueFrom(
      appResolver.resolve().pipe(
        catchError(error => {
          console.error('Data loading failed', error);
          return of(null);
        }),
      ),
    );
  };
} */

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      CommonModule,
      BrowserModule,
      BrowserAnimationsModule,
      TranslocoModule,
    ),
    importProvidersFrom(
      NgxStripeModule.forRoot(environment.stripe.key),
      QuillModule.forRoot(),
    ),
    CategoryService,
    BookingService,
    ServiceService,
    ServiceRequestService,
    WeatherService,
    PropertyService,
    UserService,
    NonWorkingDayService,
    TutorialsService,
    GoogleMapsService,
    BuildingService,
    LocationService,
    LocationRepository,
    BookingRepository,
    GeolocationService,
    GoogleMapsService,
    AddressesService,
    AgentService,
    /*  AppResolver, */
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    /*     {
      provide: APP_INITIALIZER,
      useFactory: initializeDataApp,
      deps: [AppResolver],
      multi: true,
    }, */
    provideHttpClient(withInterceptorsFromDi()),
    provideIonicAngular({
      mode: 'md',
      backButtonDefaultHref: '/',
      useSetInputAPI: true, //  required for input signals on controller based modals
    }),
    provideRouter(
      routes,
      withPreloading(NoPreloading),
      withRouterConfig({
        /**
         * @see https://github.com/angular/angular/issues/13586
         */
        canceledNavigationResolution: 'computed',
      }),
    ),
    provideQueryClientOptions({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
        },
      },
    }),
    {
      provide: LOCALE_ID,
      useFactory: (localeService: LocaleService) => localeService.getLocale(),
      deps: [LocaleService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
  ],
}).catch((error) => {
  try {
    LoggingService.log(error);
  } catch {
    console.error('Bootstrap failed:', error);
  }
});
